import React, { useEffect, useRef, useState } from 'react';
import {
  Button, Form, message, Popover, Select, UploadFile,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Autocomplete } from '@react-google-maps/api';
import Picker, { EmojiClickData } from 'emoji-picker-react';
import TextArea from 'antd/es/input/TextArea';
import { LeftOutlined } from '@ant-design/icons';

import { IconArrowLeft, IconEmogie } from '../../../Common/Icon';
import MyGoogleMap from '../../../Common/Maps';
import JoinOurCommunity from '../../TippingArticles/JoinOurCommunity';
import { SubCategoryShort, useCategory } from '../../../../store/category';
import { FileData } from '../../../../store/types';
import ImageUpload from '../../../Common/ImageUpload';
import {
  useBusinessCreate,
  useBusinessesList,
} from '../../../../hooks/api/businesses';
import { usePostsCreate } from '../../../../hooks/api/post';
import PostCard from '../BusinessSearchPage/PostCard';
import { BusinessCreateBody } from '../../../../store/business/create';
import { PostCreateBody } from '../../../../store/post/create';
import { useGoogleMapsContext } from '../../../../GoogleMapsProvider';
import Loading from '../../../Common/Loading';

import styles from './index.module.scss';

export interface FormValues {
  address: string;
  text: string;
  categoryId: string;
  subCategoryId: string;
}

export const getAddressComponent = (
  place: google.maps.places.PlaceResult | null,
  type: string,
): string => {
  if (!place) return '';

  const component = place.address_components?.find((comp) => comp.types.includes(type));

  return component?.long_name || '';
};

export default function CreateNewPostContent(): React.ReactElement {
  const { isLoaded } = useGoogleMapsContext();

  const navigate = useNavigate();
  const now = dayjs();
  const formattedDate = now.format('D MMMM, YYYY h:mm A');
  const [form] = Form.useForm<FormValues>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [subOptions, setSubOptions] = useState<SubCategoryShort[]>([]);
  const [mapCenter, setMapCenter] = useState({ lat: 51.505, lng: -0.09 });
  const [markerPosition, setMarkerPosition] = useState(mapCenter);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isViewPost, setIsViewPost] = useState(false);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [newUploadedFiles, setNewUploadedFiles] = useState<FileData[]>([]);
  const [fileDataId, setFileDataId] = useState<string[]>([]);
  const { listAll } = useCategory();
  const [categoryLabel, setCategoryLabel] = useState<string>('');
  const [businessId, setBusinessId] = useState<string | null>(null);
  const postCreate = usePostsCreate();
  const [place, setPlace] = useState<google.maps.places.PlaceResult | null>(
    null,
  );
  const [uploadKey, setUploadKey] = useState(Date.now());
  const businessCreate = useBusinessCreate();
  const businessesGetAll = useBusinessesList({ page: 1, pageSize: 100 });

  const getCategories = listAll.response?.data;

  const handleFileId = (fileId: string, resp: FileData) => {
    setFileDataId((prev) => [...prev, fileId]);
    setNewUploadedFiles((prev) => [...prev, resp]);
  };

  const handleEmojiSelect = (emojiData: EmojiClickData) => {
    const textarea = textareaRef.current;

    if (!textarea) return;

    const value = form.getFieldValue('text') || '';
    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;

    const newValue = value.slice(0, Math.max(0, start))
      + emojiData.emoji
      + value.slice(Math.max(0, end));

    form.setFieldsValue({ text: newValue });

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + emojiData.emoji.length,
        start + emojiData.emoji.length,
      );
    }, 0);

    setIsPickerOpen(false);
  };

  const onPlaceChanged = () => {
    const placeCurr = autocompleteRef.current?.getPlace() || null;

    setPlace(placeCurr);
    const location = placeCurr?.geometry?.location;

    if (location) {
      const newPos = {
        lat: location.lat(),
        lng: location.lng(),
      };

      setMapCenter(newPos);
      setMarkerPosition(newPos);

      const formatted = placeCurr.formatted_address || '';

      form.setFieldsValue({ address: formatted });

      if (inputRef.current) {
        inputRef.current.value = formatted;
      }
    }
  };

  const onMapClick = (e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      const newPos = { lat, lng };

      setMapCenter(newPos);
      setMarkerPosition(newPos);

      const geocoder = new google.maps.Geocoder();

      geocoder.geocode({ location: newPos }, (results, status) => {
        if (status === 'OK' && results && results.length > 0) {
          const firstResult = results[0];

          const placeId = firstResult.place_id;
          const formatted = firstResult.formatted_address;
          // eslint-disable-next-line max-len
          const service: google.maps.places.PlacesService = new google.maps.places.PlacesService(document.createElement('div'));

          service.getDetails({ placeId }, (placeResult, placeStatus) => {
            if (placeStatus === 'OK' && placeResult) {
              setPlace(placeResult);

              form.setFieldsValue({ address: formatted });

              if (inputRef.current) {
                inputRef.current.value = formatted;
              }
            } else {
              form.setFieldsValue({ address: formatted });

              if (inputRef.current) {
                inputRef.current.value = formatted;
              }
            }
          });
        }
      });
    }
  };

  const [formSnapshot, setFormSnapshot] = useState<FormValues | null>(null);

  const handleFinish = async (values: FormValues) => {
    await form.validateFields();

    setFormSnapshot(values);
    setIsViewPost(true);
  };

  const businessesGet = async () => {
    const data = await businessesGetAll.fetch({
      name: place?.name || '',
      address: formSnapshot?.address || '',
      page: 1,
      pageSize: 10,
    });

    const existingBusiness = data?.data?.[0];

    if (existingBusiness) {
      setBusinessId(existingBusiness.id);
    } else {
      const formValues = formSnapshot;

      if (!formValues) {
        console.error('Missing form values!');

        return;
      }

      const businessCreateBody: BusinessCreateBody = {
        name: place?.name || '',
        address: formValues.address,
        city: getAddressComponent(place, 'locality'),
        state: getAddressComponent(place, 'administrative_area_level_1'),
        country: getAddressComponent(place, 'country'),
        zipCode: getAddressComponent(place, 'postal_code'),
        // categoryIds: [formValues.categoryId],
        // subCategoryIds: [formValues.subCategoryId],
        type: categoryLabel,
        description: '',
        phone: place?.international_phone_number || '',
        website: place?.website || '',
        googleMapsInfo: {
          placeId: place?.place_id || '',
          address: place?.formatted_address || '',
          website: place?.website || '',
          url: place?.url || '',
          phoneNumber: place?.international_phone_number || '',
          fileUrls: place?.photos?.map((photo) => photo.getUrl()) || [],
          openingHoursWeekdayText:
            // @ts-ignore item do not exist fix
            place?.current_opening_hours?.weekday_text || [],
        },
        fileIds: [],
        userIds: [],
      };

      businessCreate.fetch(businessCreateBody).then((res) => {
        if (res?.id) {
          setBusinessId(res.id);
        } else {
          message.error('Something went wrong!');
        }
      });
    }
  };

  useEffect(() => {
    if (businessId && formSnapshot) {
      const filteredSnapshot = { ...formSnapshot, address: undefined };

      const payload: PostCreateBody = {
        ...filteredSnapshot,
        fileIds: fileDataId,
        businessId,
      };

      postCreate.fetch(payload).then((res) => {
        if (res?.id) {
          setFileList([]);
          setNewUploadedFiles([]);
          setFileDataId([]);
          form.resetFields();
          setPlace(null);
          setUploadKey(Date.now());
          navigate(`/share-your-two-cents/${res.id}`);
        } else {
          message.error('Something went wrong!');
        }
      });
    }
  }, [businessId, formSnapshot]);

  const submitFinalPost = async () => {
    try {
      businessesGet();
    } catch (error) {
      console.error('Failed to submit post:', error);
    }
  };

  return (
    <>
      {place?.international_phone_number}
      <div className={styles.wrapper}>
        {!isViewPost ? (
          <div className={styles.container}>
            <Button
              className={styles.btnBack}
              type="text"
              onClick={() => navigate('/share-your-two-cents')}
            >
              <IconArrowLeft className={styles.btnBackIcon} />
              Back to Share Your Two Cents
            </Button>
            <div className={styles.content}>
              <div className={styles.title}>
                <h3>Create New Post</h3>
                <span>{formattedDate}</span>
              </div>
              <Form form={form} layout="vertical" onFinish={handleFinish}>
                <h5 style={{ marginBottom: 10 }}>Find Business</h5>
                <Form.Item name="address">
                  {isLoaded ? (
                    <Autocomplete
                      // eslint-disable-next-line no-return-assign
                      onLoad={(ref) => (autocompleteRef.current = ref)}
                      onPlaceChanged={onPlaceChanged}
                    >
                      <input
                        type="text"
                        ref={inputRef}
                        placeholder="Search by Business"
                        className={styles.search}
                      />
                    </Autocomplete>
                  ) : <Loading absolute />}
                </Form.Item>
                <div className={styles.mapContainer}>
                  <MyGoogleMap
                    center={mapCenter}
                    marker={markerPosition}
                    onMapClick={onMapClick}
                  />
                </div>
                {/* <div className={styles.categorySelect}> */}
                {/*  <Form.Item */}
                {/*    name="categoryId" */}
                {/*    label="Category" */}
                {/*    className={styles.customLabel} */}
                {/*    rules={[ */}
                {/*      { required: true, message: 'Please select a category' }, */}
                {/*    ]} */}
                {/*  > */}
                {/*    <Select */}
                {/*      placeholder="Select category" */}
                {/*      size="large" */}
                {/*      onChange={(value) => { */}
                {/*        const selectedCat = getCategories?.find( */}
                {/*          (cat) => cat.id === value, */}
                {/*        ); */}

                {/*        form.setFieldsValue({ subCategoryId: undefined }); */}
                {/*        setSubOptions(selectedCat?.subCategories || []); */}
                {/*        setCategoryLabel(selectedCat?.name || ''); */}
                {/*      }} */}
                {/*      options={ */}
                {/*        getCategories?.map((cat) => ({ */}
                {/*          label: ( */}
                {/*            <div className={styles.selectedCategory}> */}
                {/*              {cat.file && ( */}
                {/*                <img */}
                {/*                  src={cat.file.location} */}
                {/*                  alt="Selected icon" */}
                {/*                  className={styles.icon} */}
                {/*                /> */}
                {/*              )} */}
                {/*              {cat.name} */}
                {/*            </div> */}
                {/*          ), */}
                {/*          value: cat.id, */}
                {/*        })) || [] */}
                {/*      } */}
                {/*    /> */}
                {/*  </Form.Item> */}
                {/*  <Form.Item */}
                {/*    name="subCategoryId" */}
                {/*    label="Subcategory" */}
                {/*    className={styles.customLabel} */}
                {/*    rules={[ */}
                {/*      { */}
                {/*        required: true, */}
                {/*        message: 'Please select a subcategory', */}
                {/*      }, */}
                {/*    ]} */}
                {/*  > */}
                {/*    <Select */}
                {/*      placeholder="Select subcategory" */}
                {/*      disabled={subOptions.length === 0} */}
                {/*      size="large" */}
                {/*      onChange={(value) => { */}
                {/*        const selectedSub = subOptions.find( */}
                {/*          (sub) => sub.id === value, */}
                {/*        ); */}
                {/*        const category = getCategories?.find( */}
                {/*          (cat) => cat.id === form.getFieldValue('categoryId'), */}
                {/*        ); */}
                {/*        const categoryName = category?.name || ''; */}
                {/*        const subName = selectedSub?.header || ''; */}

                {/*        setCategoryLabel(`${categoryName} / ${subName}`); */}
                {/*      }} */}
                {/*      options={subOptions.map((sub) => ({ */}
                {/*        label: ( */}
                {/*          <div className={styles.selectedCategory}> */}
                {/*            {sub.header} */}
                {/*          </div> */}
                {/*        ), */}
                {/*        value: sub.id, */}
                {/*      }))} */}
                {/*    /> */}
                {/*  </Form.Item> */}
                {/* </div> */}
                <div className={styles.controllsWrapper}>
                  <ImageUpload
                    key={uploadKey}
                    fileList={fileList}
                    setFileList={setFileList}
                    name="fileIds"
                    wide
                    form={form}
                    multiple
                    handleFileId={handleFileId}
                  />
                </div>
                <h5 style={{ margin: '10px 0' }}>Comments</h5>
                <div className={styles.inputsWrapper}>
                  <Form.Item name="text" className={styles.inputFormItem}>
                    <TextArea
                      className={styles.input}
                      size="large"
                      autoSize={{ minRows: 4, maxRows: 5 }}
                      placeholder="Enter your comment"
                      ref={(node) => {
                        if (node && textareaRef.current !== node) {
                          textareaRef.current = node?.resizableTextArea?.textArea || null;
                        }
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && !event.shiftKey) {
                          event.preventDefault();
                          form.submit();
                        }
                      }}
                    />
                  </Form.Item>

                  <Popover
                    content={<Picker onEmojiClick={handleEmojiSelect} />}
                    trigger="click"
                    open={isPickerOpen}
                    onOpenChange={setIsPickerOpen}
                    placement="topRight"
                  >
                    <div className={styles.emogieWrapper}>
                      <IconEmogie className={styles.emogie} />
                    </div>
                  </Popover>
                </div>
                <Form.Item style={{ textAlign: 'right' }}>
                  <Button type="primary" size="large" htmlType="submit">
                    Submit
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </div>
        ) : (
          <div
            className={styles.container}
            style={{ display: 'flex', justifyContent: 'center' }}
          >
            <div className={styles.content} style={{ maxWidth: 840 }}>
              <PostCard
                fileList={newUploadedFiles}
                isHeader
                description={form.getFieldValue('text')}
                placeName={place?.name || ''}
                address={form.getFieldValue('address')}
                categoryPath={categoryLabel}
              />
              <div className={styles.prevBtns}>
                <Button
                  className={styles.button}
                  icon={<LeftOutlined />}
                  onClick={() => setIsViewPost(false)}
                  size="large"
                >
                  Edit Post
                </Button>
                <Button
                  className={styles.button}
                  size="large"
                  type="primary"
                  onClick={submitFinalPost}
                >
                  Submit
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <JoinOurCommunity />
    </>
  );
}
